import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AuthCallback from "@/pages/AuthCallback";
import Landing from "@/pages/Landing";
import Login, { homeFor, rolesOf } from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import Dashboard from "@/pages/Dashboard";
import DemoMode from "@/pages/DemoMode";
import TestDrive from "@/pages/TestDrive";
import NewAccident from "@/pages/NewAccident";
import SubmissionConfirmation from "@/pages/SubmissionConfirmation";
import CaseDetail from "@/pages/CaseDetail";
import EvidenceVault from "@/pages/EvidenceVault";
import MyAppointments from "@/pages/MyAppointments";
import Profile from "@/pages/Profile";
import MyCases from "@/pages/MyCases";
import AttorneyPortal from "@/pages/AttorneyPortal";
import BillingCenter from "@/pages/BillingCenter";
import AdminPortal from "@/pages/AdminPortal";
import AccessPending from "@/pages/AccessPending";
import ScoreCase from "@/pages/ScoreCase";
import Marketplace from "@/pages/Marketplace";
import IntegrationsPage from "@/pages/IntegrationsPage";
import Developer from "@/pages/Developer";
import Intelligence from "@/pages/Intelligence";
import CaseIntelligence from "@/pages/CaseIntelligence";
import FirmSettings from "@/pages/FirmSettings";
import CommandCenter from "@/pages/CommandCenter";import AffiliatePortal from "@/pages/AffiliatePortal";
import PilotPortal from "@/pages/PilotPortal";
import NursePortal from "@/pages/NursePortal";
import TelehealthPortal from "@/pages/TelehealthPortal";
import PilotHUD from "@/pages/PilotHUD";
import AttorneyPending from "@/pages/AttorneyPending";
import PaymentReturn from "@/pages/PaymentReturn";
import MembershipGuard from "@/components/MembershipGuard";
import RedirectCode from "@/pages/RedirectCode";
import SharePage from "@/pages/SharePage";
import VerifiedBadgePage from "@/pages/VerifiedBadgePage";
import DemoExperience from "@/components/DemoExperience";
import ComplianceCenter from "@/pages/ComplianceCenter";
import MySecurity from "@/pages/MySecurity";
import TrustCenterPage from "@/pages/TrustCenterPage";
import EnterpriseReadiness from "@/pages/EnterpriseReadiness";
import DemoControlCenter from "@/pages/DemoControlCenter";
import SecuritySuite from "@/pages/SecuritySuite";
import SecurityPackagePage from "@/pages/SecurityPackagePage";
import WorkspacePortal from "@/pages/WorkspacePortal";

const Spinner = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="h-10 w-10 rounded-full border-2 border-cobalt border-t-transparent animate-spin" />
  </div>
);

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// Restricts a route to specific roles. Approved (active) attorneys and admins
// only. Regular users are bounced to their own home; suspended attorneys are
// sent to the access-pending screen.
function RoleRoute({ children, allow }) {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  const roles = rolesOf(user);
  if (allow.includes("attorney") && roles.includes("attorney") && !roles.includes("admin") && user.attorney_status === "suspended") {
    return <Navigate to="/access-pending" replace />;
  }
  if (!allow.some((r) => roles.includes(r))) return <Navigate to={homeFor(user)} replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  // Process Emergent OAuth session_id synchronously BEFORE any route renders.
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/share/:token" element={<SharePage />} />
      <Route path="/security-package/:token" element={<SecurityPackagePage />} />
      <Route path="/verified/:token" element={<VerifiedBadgePage />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ForgotPassword />} />
      <Route path="/access-pending" element={<ProtectedRoute><AccessPending /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/demo" element={<DemoMode />} />
      <Route path="/test-drive" element={<TestDrive />} />
      <Route path="/cases" element={<ProtectedRoute><MyCases /></ProtectedRoute>} />
      <Route path="/new-accident" element={<ProtectedRoute><NewAccident /></ProtectedRoute>} />
      <Route path="/cases/:caseId" element={<ProtectedRoute><CaseDetail /></ProtectedRoute>} />
      <Route path="/cases/:caseId/submitted" element={<ProtectedRoute><SubmissionConfirmation /></ProtectedRoute>} />
      <Route path="/cases/:caseId/score" element={<ProtectedRoute><ScoreCase /></ProtectedRoute>} />
      <Route path="/evidence" element={<ProtectedRoute><EvidenceVault /></ProtectedRoute>} />
      <Route path="/appointments" element={<ProtectedRoute><MyAppointments /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="/attorney/membership/success" element={<ProtectedRoute><PaymentReturn /></ProtectedRoute>} />
      <Route path="/attorney/payment/success" element={<ProtectedRoute><PaymentReturn /></ProtectedRoute>} />
      <Route path="/attorney" element={<RoleRoute allow={["attorney", "admin"]}><MembershipGuard><AttorneyPortal /></MembershipGuard></RoleRoute>} />
      <Route path="/attorney/billing" element={<RoleRoute allow={["attorney", "admin"]}><BillingCenter /></RoleRoute>} />
      <Route path="/marketplace" element={<RoleRoute allow={["attorney", "admin"]}><MembershipGuard><Marketplace /></MembershipGuard></RoleRoute>} />
      <Route path="/integrations" element={<RoleRoute allow={["attorney", "admin"]}><MembershipGuard><IntegrationsPage /></MembershipGuard></RoleRoute>} />
      <Route path="/developer" element={<RoleRoute allow={["attorney", "admin"]}><MembershipGuard><Developer /></MembershipGuard></RoleRoute>} />
      <Route path="/intelligence" element={<RoleRoute allow={["attorney", "admin"]}><MembershipGuard><Intelligence /></MembershipGuard></RoleRoute>} />
      <Route path="/intelligence/:caseId" element={<RoleRoute allow={["attorney", "admin"]}><MembershipGuard><CaseIntelligence /></MembershipGuard></RoleRoute>} />
      <Route path="/firm" element={<RoleRoute allow={["attorney", "admin"]}><MembershipGuard><FirmSettings /></MembershipGuard></RoleRoute>} />
      <Route path="/command-center" element={<RoleRoute allow={["attorney", "admin"]}><MembershipGuard><CommandCenter /></MembershipGuard></RoleRoute>} />
      <Route path="/affiliate" element={<RoleRoute allow={["affiliate", "admin"]}><AffiliatePortal /></RoleRoute>} />
      <Route path="/pilot-portal" element={<ProtectedRoute><PilotPortal /></ProtectedRoute>} />
      <Route path="/nurse-portal" element={<ProtectedRoute><NursePortal /></ProtectedRoute>} />
      <Route path="/telehealth-portal" element={<ProtectedRoute><TelehealthPortal /></ProtectedRoute>} />
      <Route path="/pilot/hud" element={<ProtectedRoute><PilotHUD /></ProtectedRoute>} />
      <Route path="/attorney-pending" element={<ProtectedRoute><AttorneyPending /></ProtectedRoute>} />
      <Route path="/r/:code" element={<RedirectCode />} />
      <Route path="/admin" element={<RoleRoute allow={["admin"]}><AdminPortal /></RoleRoute>} />
      <Route path="/compliance" element={<RoleRoute allow={["admin"]}><ComplianceCenter /></RoleRoute>} />
      <Route path="/enterprise-readiness" element={<RoleRoute allow={["admin"]}><EnterpriseReadiness /></RoleRoute>} />
      <Route path="/demo-control" element={<RoleRoute allow={["admin"]}><DemoControlCenter /></RoleRoute>} />
      <Route path="/security-suite" element={<RoleRoute allow={["admin"]}><SecuritySuite /></RoleRoute>} />
      <Route path="/workspace/:token" element={<WorkspacePortal />} />
      <Route path="/trust-center" element={<TrustCenterPage />} />
      <Route path="/security" element={<ProtectedRoute><MySecurity /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <DemoExperience />
          <Toaster position="top-center" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
